"use client";

// eslint-disable-next-line no-restricted-imports -- the one place that wraps it
import { c as _c } from "react/compiler-runtime";
import NextLink from "next/link";
import { isRewrittenToAppRouter } from "@lib/cms/v2Slugs";
import { localizePath } from "@lib/i18n/locale";
import useRouteLocale from "@lib/hooks/useRouteLocale";
import { useRouter as useCompatRouter } from "next/compat/router";
import { ComponentProps, forwardRef } from "react";
type Href = ComponentProps<typeof NextLink>["href"];
const localizeHref = (locale: Parameters<typeof localizePath>[0], href: Href): Href => typeof href === "string" ? localizePath(locale, href) : href.pathname && !href.pathname.includes("[") ? {
  ...href,
  pathname: localizePath(locale, href.pathname)
} : href;

/** `next/link` that keeps the visitor in the locale of the page they are on. */
const LocaleLink = forwardRef<HTMLAnchorElement, ComponentProps<typeof NextLink>>(function LocaleLink(t0, ref) {
  const $ = _c(29);
  let as;
  let href;
  let props;
  if ($[0] !== t0) {
    ({
      href,
      as,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = as;
    $[2] = href;
    $[3] = props;
  } else {
    as = $[1];
    href = $[2];
    props = $[3];
  }
  const locale = useRouteLocale();
  const pagesRouter = useCompatRouter();
  let t1;
  let target;
  if ($[4] !== as || $[5] !== href || $[6] !== locale || $[7] !== pagesRouter) {
    target = localizeHref(locale, as ?? href);
    t1 = pagesRouter && typeof target === "string" && isRewrittenToAppRouter(target);
    $[4] = as;
    $[5] = href;
    $[6] = locale;
    $[7] = pagesRouter;
    $[8] = t1;
    $[9] = target;
  } else {
    t1 = $[8];
    target = $[9];
  }
  if (t1) {
    let anchorProps;
    let children;
    if ($[10] !== props) {
      const {
        prefetch: _prefetch,
        replace: _replace,
        scroll: _scroll,
        shallow: _shallow,
        passHref: _passHref,
        locale: _locale,
        onNavigate: _onNavigate,
        children: t2,
        ...t3
      } = props;
      children = t2;
      anchorProps = t3;
      $[10] = props;
      $[11] = anchorProps;
      $[12] = children;
    } else {
      anchorProps = $[11];
      children = $[12];
    }
    let t2;
    if ($[13] !== anchorProps || $[14] !== children || $[15] !== ref || $[16] !== target) {
      t2 = <a {...anchorProps} ref={ref} href={target}>{children}</a>;
      $[13] = anchorProps;
      $[14] = children;
      $[15] = ref;
      $[16] = target;
      $[17] = t2;
    } else {
      t2 = $[17];
    }
    return t2;
  }
  let t2;
  if ($[18] !== href || $[19] !== locale) {
    t2 = localizeHref(locale, href);
    $[18] = href;
    $[19] = locale;
    $[20] = t2;
  } else {
    t2 = $[20];
  }
  let t3;
  if ($[21] !== as || $[22] !== locale) {
    t3 = as && localizeHref(locale, as);
    $[21] = as;
    $[22] = locale;
    $[23] = t3;
  } else {
    t3 = $[23];
  }
  let t4;
  if ($[24] !== props || $[25] !== ref || $[26] !== t2 || $[27] !== t3) {
    t4 = <NextLink {...props} ref={ref} href={t2} as={t3} />;
    $[24] = props;
    $[25] = ref;
    $[26] = t2;
    $[27] = t3;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  return t4;
});
export default LocaleLink;